import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./contexts/AuthContext";
import { NotificationsProvider } from "./contexts/NotificationsContext";
import { MessagesProvider } from "./contexts/MessagesContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/layout/Layout";
import PublicLayout from "./components/layout/PublicLayout";
import { ProtectedRoute } from "./components/RouteGuards";
import Auth from "./pages/Auth";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Home from "./pages/Home";
import Feed from "./pages/Feed";
import PostDetail from "./pages/PostDetail";
import CreatePost from "./pages/CreatePost";
import AIChat from "./pages/AIChat";
import Category from "./pages/Category";
import Profile from "./pages/Profile";
import Subscription from "./pages/Subscription";
import Settings from "./pages/Settings";
import Notifications from "./pages/Notifications";
import Messages from "./pages/Messages";
import { HowItWorks, Privacy, Terms, Contact, RefundPolicy } from "./pages/Legal";
import Admin from "./pages/Admin";
import PaymentSuccess from "./pages/PaymentSuccess";

function App() {
    return (
        <HelmetProvider>
            <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
                <AuthProvider>
                    <BrowserRouter>
                        <NotificationsProvider>
                            <MessagesProvider>
                            <Routes>
                            <Route path="/auth" element={<Auth />} />
                            <Route path="/forgot-password" element={<PublicLayout />}>
                                <Route index element={<ForgotPassword />} />
                            </Route>
                            <Route path="/reset-password" element={<PublicLayout />}>
                                <Route index element={<ResetPassword />} />
                            </Route>
                            {/* Legal & info pages — publicly accessible so prospects
                                can read Terms / Privacy / How-It-Works before signing up. */}
                            <Route element={<PublicLayout />}>
                                <Route path="/privacy" element={<Privacy />} />
                                <Route path="/terms" element={<Terms />} />
                                <Route path="/refund-policy" element={<RefundPolicy />} />
                                <Route path="/refunds" element={<RefundPolicy />} />
                                <Route path="/contact" element={<Contact />} />
                                <Route path="/how-it-works" element={<HowItWorks />} />
                                <Route path="/subscription" element={<Subscription />} />
                            </Route>
                            <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />

                            {/* Public-readable content surfaces. Logged-out
                                visitors can browse Home / Feed / Idea pages /
                                Category pages — Google can crawl them, users
                                can share them. Interactive actions (like,
                                comment, post, message) still prompt sign-in.
                                Non-Sample admin posts return a teaser body so
                                the Pro paywall stays intact. */}
                            <Route element={<Layout />}>
                                <Route path="/" element={<Home />} />
                                <Route path="/feed" element={<Feed />} />
                                <Route path="/post/:id" element={<PostDetail />} />
                                <Route path="/category/:slug" element={<Category />} />
                            </Route>

                            {/* Anything that creates content, costs money or
                                touches a personal inbox still requires sign-in. */}
                            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                                <Route path="/create" element={<CreatePost />} />
                                <Route path="/ai-chat" element={<AIChat />} />
                                <Route path="/profile" element={<Profile />} />
                                <Route path="/settings" element={<Settings />} />
                                <Route path="/notifications" element={<Notifications />} />
                                <Route path="/messages" element={<Messages />} />
                                <Route path="/messages/:conversationId" element={<Messages />} />
                                <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
                                <Route path="*" element={<Navigate to="/" replace />} />
                            </Route>
                            </Routes>
                            </MessagesProvider>
                        </NotificationsProvider>
                        <Toaster position="top-right" richColors />
                    </BrowserRouter>
                </AuthProvider>
            </ThemeProvider>
        </HelmetProvider>
    );
}

export default App;
